import { Fragment, ReactNode, useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { KeyNames } from '../../../constants';
import { DynamicFieldIconSmall } from '../../../icon';
import { NakedButton } from '../../../naked';
import { Button } from '../../button';
import { PopoverDialog, usePopoverDialog } from '../../popover';
import { Text } from '../../text';
import { ActionPressedChip } from '../atoms';
import { useEditorContext } from '../providers';

type DynamicFieldBaseAttributes = { label: string; symbol: string; val?: string };
export type DynamicFieldAttributes = DynamicFieldBaseAttributes & {
  customSelect?: boolean;
  options?: DynamicFieldBaseAttributes[];
};

const SIDE_PADDING = 28;

export type SharedDynamicFieldsProps = {
  correspondingDynamicFields?: Record<string, DynamicFieldAttributes[]>;
  sharedDynamicFields?: DynamicFieldAttributes[];
  setSharedDynamicFields?: (fields: DynamicFieldAttributes[]) => void;
};

type DynamicFieldButtonProps = {
  field: DynamicFieldAttributes;
  onSelect: (selection: DynamicFieldAttributes) => void;
};

const DynamicFieldButtonWithOptions = ({ field, onSelect }: DynamicFieldButtonProps) => {
  const { sharedDynamicFieldProps = {} } = useEditorContext();
  const { correspondingDynamicFields, setSharedDynamicFields, sharedDynamicFields } = sharedDynamicFieldProps;
  const [selectedDynamicField, setSelectedDynamicField] = useState<DynamicFieldAttributes>(field);
  const popoverRef = useRef<HTMLDialogElement>(null);

  const [offsetX, setOffsetX] = useState(0);
  const offsetY = 24;

  const popover = usePopoverDialog({
    placement: 'bottom-end',
    initialOffset: {
      x: offsetX,
      y: offsetY,
    },
  });

  useEffect(() => {
    const newSelectedDynamicField = sharedDynamicFields?.find((t) => t.symbol === selectedDynamicField.symbol);
    if (newSelectedDynamicField) {
      setSelectedDynamicField(newSelectedDynamicField);
    }
  }, [selectedDynamicField.symbol, sharedDynamicFields]);

  useEffect(() => {
    // Popover dropdown needs to be dynamically offset so it's symmetrically positioned beneath the tag
    if (!popover.isOpen || !popoverRef.current) return;
    const dropdownWidth = popoverRef.current.offsetWidth;
    setOffsetX(-dropdownWidth + SIDE_PADDING);
  }, [popover.isOpen]);

  const handleOptionSelect = (dynamicField: DynamicFieldAttributes) => {
    setSelectedDynamicField(dynamicField);
    if (dynamicField.symbol && correspondingDynamicFields && correspondingDynamicFields?.[dynamicField.label]) {
      setSharedDynamicFields?.(correspondingDynamicFields[dynamicField.label] as DynamicFieldAttributes[]);
    }
    onSelect(dynamicField);
    popover.close();
  };

  return (
    <Fragment key={field.symbol}>
      <Button css={buttonStyles} size='small' onClick={() => onSelect(selectedDynamicField)} variant='secondary'>
        {selectedDynamicField.label}
        <NakedButton
          {...popover.getTriggerProps()}
          onClick={(e) => {
            e.stopPropagation();
            popover.getTriggerProps().onClick?.(e);
          }}
          css={{ padding: `${theme.spacing(0, 0.5)} !important` }}
        >
          <Icon
            name='caret-down-small'
            css={{
              transition: 'transform 200ms ease-out',
              transform: popover?.isOpen ? 'rotate(180deg)' : 'inherit',
            }}
          />
        </NakedButton>
      </Button>
      {field?.options ? (
        <PopoverDialog css={{ padding: theme.spacing(1, 0) }} {...popover.getDialogProps()} ref={popoverRef}>
          {field.options.map((dynamicField, index) => (
            <Text
              as='li'
              size='small'
              key={index}
              tabIndex={0}
              css={{
                listStyle: 'none',
                padding: theme.spacing(1, 2),
                cursor: 'pointer',
              }}
              onClick={() => handleOptionSelect(dynamicField)}
              onKeyDown={(e) => {
                if (e.key === KeyNames.Enter) handleOptionSelect(dynamicField);
              }}
            >
              {dynamicField.label}
            </Text>
          ))}
        </PopoverDialog>
      ) : null}
    </Fragment>
  );
};

const DynamicFieldButton = ({ field, onSelect }: DynamicFieldButtonProps) => (
  <Button key={field.symbol} css={buttonStyles} size='small' onClick={() => onSelect(field)} variant='secondary'>
    {field.label}
  </Button>
);

const buttonStyles = {
  fontWeight: theme.font.weight.regular,
  height: theme.spacing(3),
  fontSize: theme.fontSize(12),
};

type DynamicFieldActionProps = {
  fields: DynamicFieldAttributes[];
  label?: ReactNode;
  onSelect: (selection: DynamicFieldAttributes) => void;
} & Pick<React.ComponentProps<typeof ActionPressedChip>, 'showBoxShadow' | 'compact' | 'css' | 'isActive'>;

export const DynamicFieldAction = ({
  compact = true,
  fields,
  isActive,
  label,
  onSelect,
  showBoxShadow = true,
  ...rest
}: DynamicFieldActionProps) => {
  const { getDialogProps, getTriggerProps, isOpen } = usePopoverDialog<HTMLButtonElement | HTMLAnchorElement>({
    placement: 'bottom-start',
    middlewareOptions: {
      offset: {
        crossAxis: 0,
        mainAxis: 12,
      },
    },
  });

  return (
    <>
      <ActionPressedChip
        showBoxShadow={showBoxShadow}
        isActive={isOpen || isActive}
        compact={compact}
        {...getTriggerProps()}
        {...rest}
      >
        {label ?? (
          <>
            <DynamicFieldIconSmall style={{ marginRight: theme.spacing(1) }} /> Dynamic Field
          </>
        )}
      </ActionPressedChip>
      <PopoverDialog
        {...getDialogProps()}
        returnFocus={false}
        css={css`
          display: flex;
          flex-wrap: wrap;
          gap: ${theme.spacing(1)};
          max-width: 400px;
          width: 400px;
          border-radius: ${theme.borderRadius.medium};
          button {
            height: auto;
            width: auto;
            padding: 2px 8px;
          }
        `}
      >
        {fields.map((field) =>
          field.options ? (
            <DynamicFieldButtonWithOptions key={field.symbol} field={field} onSelect={onSelect} />
          ) : (
            <DynamicFieldButton key={field.symbol} field={field} onSelect={onSelect} />
          )
        )}
      </PopoverDialog>
    </>
  );
};
